import { useState } from 'react';
import {
  Modal,
  Button,
  Group,
  Text,
  Stack,
  Alert,
  List,
  Progress,
  Anchor,
} from '@mantine/core';
import { Dropzone, MS_EXCEL_MIME_TYPE } from '@mantine/dropzone';
import { IconUpload, IconFileTypeCsv, IconX, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { readFileAsText, parseCSV, validateAssetImport, type AssetImportRow } from '@/lib/import';

interface ImportAssetsModalProps {
  opened: boolean;
  onClose: () => void;
  onImport: (data: AssetImportRow[]) => Promise<void>;
  isLoading?: boolean;
}

export function ImportAssetsModal({
  opened,
  onClose,
  onImport,
  isLoading,
}: ImportAssetsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    errors: string[];
    data?: AssetImportRow[];
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleDrop = async (files: File[]) => {
    const selectedFile = files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setValidationResult(null);
    setIsValidating(true);

    try {
      // Read and parse file
      const text = await readFileAsText(selectedFile);
      const data = parseCSV(text);

      // Validate data
      const result = validateAssetImport(data);
      setValidationResult(result);
    } catch (error) {
      setValidationResult({
        valid: false,
        errors: ['Failed to parse file. Please ensure it is a valid CSV file.'],
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!validationResult?.data) return;
    await onImport(validationResult.data);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setValidationResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const template = `tag_id,description,brand,model,serial_number,status,cost,purchase_date,purchased_from,warranty_period,category,location,department
LAP-001,MacBook Pro 16-inch,Apple,MacBook Pro 2023,C02XY1ZGJHD,Available,2499.99,2024-01-15,Apple Store,36,Computers,Office,IT
DESK-001,Standing Desk,Uplift,V2,UD12345,Available,799.99,2024-02-01,Uplift Desk,60,Furniture,Office,Operations`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'asset_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Import Assets" size="lg">
      <Stack gap="md">
        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          <Text size="sm">
            Upload a CSV file with asset data. Required columns: tag_id, description, brand,
            model, serial_number, status, purchase_date, purchased_from.
          </Text>
          <Anchor size="sm" onClick={downloadTemplate} style={{ cursor: 'pointer' }}>
            Download CSV template
          </Anchor>
        </Alert>

        {!file ? (
          <Dropzone
            onDrop={handleDrop}
            accept={[...MS_EXCEL_MIME_TYPE, 'text/csv']}
            maxFiles={1}
            loading={isValidating}
          >
            <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload size={52} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={52} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFileTypeCsv size={52} stroke={1.5} />
              </Dropzone.Idle>

              <Stack gap="xs" align="center">
                <Text size="xl" inline>
                  Drag CSV file here or click to select
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  Supports .csv files only
                </Text>
              </Stack>
            </Group>
          </Dropzone>
        ) : (
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Selected file: {file.name}
              </Text>
              <Button
                variant="subtle"
                size="xs"
                onClick={() => {
                  setFile(null);
                  setValidationResult(null);
                }}
              >
                Remove
              </Button>
            </Group>

            {isValidating && (
              <Stack gap="xs">
                <Text size="sm">Validating file...</Text>
                <Progress value={100} animated />
              </Stack>
            )}

            {validationResult && (
              <>
                {validationResult.valid ? (
                  <Alert icon={<IconCheck size={16} />} color="green">
                    <Text size="sm" fw={500} mb="xs">
                      Validation successful!
                    </Text>
                    <Text size="sm">
                      Found {validationResult.data?.length || 0} valid asset(s) ready to import.
                    </Text>
                  </Alert>
                ) : (
                  <Alert icon={<IconAlertCircle size={16} />} color="red">
                    <Text size="sm" fw={500} mb="xs">
                      Validation failed
                    </Text>
                    <List size="sm" spacing="xs">
                      {validationResult.errors.slice(0, 10).map((error, i) => (
                        <List.Item key={i}>{error}</List.Item>
                      ))}
                      {validationResult.errors.length > 10 && (
                        <List.Item>
                          ... and {validationResult.errors.length - 10} more errors
                        </List.Item>
                      )}
                    </List>
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!validationResult?.valid || isLoading}
            loading={isLoading}
          >
            Import {validationResult?.data?.length || 0} Asset(s)
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
